import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMode } from '../contexts/ModeContext';
import { useSpace } from '../contexts/SpaceContext';
import { API_ENDPOINTS } from '../config';
import { UnifiedHeader } from './UnifiedHeader';
import { ModeType } from '../contexts/ModeContext';
import AddRecipeSheet from './AddRecipeSheet';
import RecipeShareModal from './RecipeShareModal';
import CollectionShareModal from './CollectionShareModal';
import ContextHelpTip from './ContextHelpTip';
import '../styles/components/recipesTab.css';
import '../styles/components/recipeShareModal.css';

interface EditIngredient {
  tempId: number;
  name: string;
  quantity: string;
  unit: string;
}

interface RecipeIngredient {
  id: number;
  name: string;
  quantity: number | null;
  unit: string | null;
  notes: string | null;
  sort_order: number;
}

interface Recipe {
  id: number;
  name: string;
  instructions: string | null;
  source: string;
  source_url: string | null;
  servings: number | null;
  prep_time_minutes: number | null;
  cook_time_minutes: number | null;
  calories_per_serving: number | null;
  spoonacular_id?: number | null;
  spoonacular_score?: number | null;
  library_recipe_id?: number | null;
  is_library?: boolean;
  is_checked_out?: boolean;
  forked_recipe_id?: number | null;
  ingredients: RecipeIngredient[];
}

interface RecipesTabProps {
  isActive: boolean;
  groceryCollectionId: number | null;
  onActivateRecipe: (recipe: { name: string; description: string | null; search_string: string }) => void;
  onFontSizeToggle: () => void;
  fontSize: string;
  activeMode: ModeType;
  onOpenAppContext: () => void;
}

const RecipesTab: React.FC<RecipesTabProps> = ({
  isActive,
  groceryCollectionId,
  onActivateRecipe,
  onFontSizeToggle,
  fontSize,
  activeMode,
  onOpenAppContext
}) => {
  const { token, user } = useAuth();
  const { activeSpace } = useSpace();
  const { ensureGroceryCollection } = useMode();

  // Self-healing: if groceryCollectionId is null when this tab is active, set it up directly
  useEffect(() => {
    if (!isActive || groceryCollectionId || !token || !activeSpace?.id) return;
    let cancelled = false;
    const spaceId = activeSpace.id;
    console.log('[RecipesTab] groceryCollectionId is null, calling ensureGroceryCollection...');
    (async () => {
      for (let attempt = 0; attempt < 5 && !cancelled; attempt++) {
        const result = await ensureGroceryCollection(token, spaceId);
        if (result || cancelled) break;
        await new Promise(r => setTimeout(r, 2000));
      }
    })();
    return () => { cancelled = true; };
  }, [isActive, groceryCollectionId, token, activeSpace?.id, ensureGroceryCollection]);

  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [openRecipeId, setOpenRecipeId] = useState<number | null>(null);
  const [expandedIngredients, setExpandedIngredients] = useState<Set<number>>(new Set());
  const [editingRecipeId, setEditingRecipeId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{ name: string; instructions: string; ingredients: EditIngredient[] } | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  let nextTempId = useRef(0);

  // Library view state
  const [recipeView, setRecipeView] = useState<'mine' | 'library'>('mine');
  const [libraryRecipes, setLibraryRecipes] = useState<Recipe[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [checkingOutId, setCheckingOutId] = useState<number | null>(null);

  // Fork dialog state
  const [forkConfirmRecipe, setForkConfirmRecipe] = useState<Recipe | null>(null);
  const [isForkingId, setIsForkingId] = useState<number | null>(null);

  // Share state
  const [shareRecipe, setShareRecipe] = useState<Recipe | null>(null);
  const [showCollectionShare, setShowCollectionShare] = useState(false);

  const startEdit = (recipe: Recipe) => {
    nextTempId.current = 0;
    setEditForm({
      name: recipe.name,
      instructions: recipe.instructions || '',
      ingredients: recipe.ingredients.map(i => ({
        tempId: nextTempId.current++,
        name: i.name,
        quantity: i.quantity != null ? String(i.quantity) : '',
        unit: i.unit || '',
      })),
    });
    setEditError(null);
    setEditingRecipeId(recipe.id);
  };

  const cancelEdit = () => {
    setEditingRecipeId(null);
    setEditForm(null);
    setEditError(null);
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Add temporary padding to bottom of scrollable area for keyboard
    const scrollable = e.target.closest('.recipes-tab-container') as HTMLElement;
    if (scrollable) {
      const originalPadding = scrollable.style.paddingBottom;
      scrollable.style.paddingBottom = '300px';
      
      // Scroll to the input
      setTimeout(() => {
        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      
      // Remove padding when input loses focus
      const restorePadding = () => {
        scrollable.style.paddingBottom = originalPadding;
        e.target.removeEventListener('blur', restorePadding);
      };
      e.target.addEventListener('blur', restorePadding);
    }
  };

  const deleteRecipe = async (recipeId: number, recipeName: string) => {
    if (!window.confirm(`Delete "${recipeName}"?`)) return;
    try {
      const resp = await fetch(API_ENDPOINTS.DELETE_RECIPE(recipeId), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        // Remove from local state immediately
        setRecipes(prev => prev.filter(r => r.id !== recipeId));
        setLibraryRecipes(prev => prev.filter(r => r.id !== recipeId));
        if (openRecipeId === recipeId) setOpenRecipeId(null);
        
        // Delay refresh to ensure server deletion has time to process
        setTimeout(() => {
          if (recipeView === 'library') {
            fetchLibraryRecipes();
          }
        }, 500);
      }
    } catch {
      // silent — non-critical
    }
  };

  const saveEdit = async () => {
    if (!editForm || !editingRecipeId || !token) return;
    if (!editForm.name.trim()) { setEditError('Name is required'); return; }
    setEditSaving(true);
    setEditError(null);
    try {
      const resp = await fetch(API_ENDPOINTS.UPDATE_RECIPE(editingRecipeId), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editForm.name.trim(),
          instructions: editForm.instructions.trim() || null,
          ingredients: editForm.ingredients
            .filter(i => i.name.trim())
            .map((i, idx) => ({
              name: i.name.trim(),
              quantity: i.quantity !== '' ? Number(i.quantity) : null,
              unit: i.unit.trim() || null,
              sort_order: idx,
            })),
        }),
      });
      if (!resp.ok) {
        const d = await resp.json();
        setEditError(d.error || 'Failed to save');
        return;
      }
      const updated: Recipe = await resp.json();
      setRecipes(prev => prev.map(r => r.id === updated.id ? updated : r));
      setEditingRecipeId(null);
      setEditForm(null);
    } catch {
      setEditError('Failed to save');
    } finally {
      setEditSaving(false);
    }
  };

  const addIngredientRow = () => {
    setEditForm(prev => prev ? {
      ...prev,
      ingredients: [...prev.ingredients, { tempId: nextTempId.current++, name: '', quantity: '', unit: '' }],
    } : prev);
  };

  const removeIngredientRow = (tempId: number) => {
    setEditForm(prev => prev ? {
      ...prev,
      ingredients: prev.ingredients.filter(i => i.tempId !== tempId),
    } : prev);
  };

  const updateIngredientRow = (tempId: number, field: keyof EditIngredient, value: string) => {
    setEditForm(prev => prev ? {
      ...prev,
      ingredients: prev.ingredients.map(i => i.tempId === tempId ? { ...i, [field]: value } : i),
    } : prev);
  };

  const toggleIngredients = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIngredients(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const fetchRecipes = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(API_ENDPOINTS.GET_RECIPES, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setRecipes(Array.isArray(data?.recipes) ? data.recipes : []);
      } else {
        setError('Failed to load recipes');
      }
    } catch {
      setError('Failed to load recipes');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchLibraryRecipes = useCallback(async (q?: string) => {
    if (!token) return;
    setLibraryLoading(true);
    setLibraryError(null);
    try {
      const url = new URL(API_ENDPOINTS.GET_LIBRARY_RECIPES);
      if (q) url.searchParams.set('q', q);
      const resp = await fetch(url.toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setLibraryRecipes(Array.isArray(data?.recipes) ? data.recipes : []);
      } else {
        setLibraryError('Failed to load library');
      }
    } catch {
      setLibraryError('Failed to load library');
    } finally {
      setLibraryLoading(false);
    }
  }, [token]);

  const handleCheckout = async (recipe: Recipe) => {
    if (!token) return;
    setCheckingOutId(recipe.id);
    try {
      const resp = await fetch(API_ENDPOINTS.CHECKOUT_LIBRARY_RECIPE(recipe.id), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        setLibraryRecipes(prev => prev.map(r => r.id === recipe.id ? { ...r, is_checked_out: true } : r));
        fetchRecipes();
      }
    } catch {
      // silent
    } finally {
      setCheckingOutId(null);
    }
  };

  const handleRemoveCheckout = async (recipe: Recipe) => {
    if (!token) return;
    try {
      await fetch(API_ENDPOINTS.CHECKOUT_LIBRARY_RECIPE(recipe.id), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setLibraryRecipes(prev => prev.map(r => r.id === recipe.id ? { ...r, is_checked_out: false } : r));
      setRecipes(prev => prev.filter(r => r.id !== recipe.id));
      if (openRecipeId === recipe.id) setOpenRecipeId(null);
    } catch {
      // silent
    }
  };

  const handleFork = async (recipe: Recipe) => {
    if (!token || !recipe.id) return;
    setIsForkingId(recipe.id);
    try {
      const resp = await fetch(API_ENDPOINTS.FORK_LIBRARY_RECIPE(recipe.id), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (resp.ok) {
        const forked = await resp.json();
        setRecipes(prev => {
          const without = prev.filter(r => r.id !== recipe.id);
          return [...without, forked].sort((a, b) => a.name.localeCompare(b.name));
        });
        setOpenRecipeId(forked.id);
        setForkConfirmRecipe(null);
        setLibraryRecipes(prev => prev.map(r => r.id === recipe.id ? { ...r, forked_recipe_id: forked.id } : r));
      }
    } catch {
      // silent
    } finally {
      setIsForkingId(null);
    }
  };

  useEffect(() => {
    if (isActive) {
      fetchRecipes();
    }
  }, [isActive, fetchRecipes]);

  useEffect(() => {
    if (isActive && recipeView === 'library') {
      fetchLibraryRecipes();
    }
  }, [isActive, recipeView, fetchLibraryRecipes]);

  useEffect(() => {
    const handler = () => { fetchRecipes(); if (recipeView === 'library') fetchLibraryRecipes(); };
    window.addEventListener('recipes-updated', handler);
    return () => window.removeEventListener('recipes-updated', handler);
  }, [fetchRecipes, fetchLibraryRecipes, recipeView]);

  const filteredRecipes = recipes.filter(r => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      r.name.toLowerCase().includes(q) ||
      (r.instructions || '').toLowerCase().includes(q) ||
      r.ingredients.some(i => i.name.toLowerCase().includes(q))
    );
  });

  const renderLibraryRecipeCard = (recipe: Recipe) => {
  const isOpen = openRecipeId === recipe.id;
  const { ingredients } = recipe;
  const ingredientsExpanded = expandedIngredients.has(recipe.id);
  const isCheckedOut = !!recipe.is_checked_out;
  const isForked = !!recipe.forked_recipe_id;

  if (!isOpen) {
    return (
      <div key={recipe.id} className={`recipe-row recipe-row--library-view${isCheckedOut ? ' recipe-row--library' : ''}`}>
        <input
          type="checkbox"
          className="recipe-row-checkbox"
          checked={isCheckedOut || isForked}
          onChange={(e) => {
            e.stopPropagation();
            if (e.target.checked) {
              // Add to Mine immediately
              if (!isCheckedOut && !isForked) {
                handleCheckout(recipe);
              }
            } else {
              // Remove from Mine immediately
              if (isCheckedOut || isForked) {
                handleRemoveCheckout(recipe);
              }
            }
          }}
                  />
        <button
          className="recipe-row-name"
          onClick={() => setOpenRecipeId(recipe.id)}
        >
          {recipe.name}
        </button>
        {isForked && (
          <span className="recipe-shared-badge">Added</span>
        )}
        <button
          className="recipe-row-chevron-btn"
          onClick={() => setOpenRecipeId(recipe.id)}
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} className="recipe-row-chevron">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div key={recipe.id} className="recipe-card">
      <div className="recipe-card-header">
        <input
          type="checkbox"
          className="recipe-card-checkbox"
          checked={isCheckedOut || isForked}
          onChange={(e) => {
            if (e.target.checked) {
              // Add to Mine immediately
              if (!isCheckedOut && !isForked) {
                handleCheckout(recipe);
              }
            } else {
              // Remove from Mine immediately
              if (isCheckedOut || isForked) {
                handleRemoveCheckout(recipe);
              }
            }
          }}
                  />
        <span className="recipe-card-name">
          {recipe.name}
          {isForked && (
            <span className="recipe-shared-badge">Added</span>
          )}
        </span>
        <button
          className="recipe-card-close"
          onClick={() => setOpenRecipeId(null)}
          aria-label="Close"
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </button>
      </div>

      {ingredients.length > 0 && (
        <div className="recipe-card-ingredients-section">
          <button
            className="recipe-card-ingredients-toggle"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedIngredients(prev => {
                const next = new Set(prev);
                next.has(recipe.id) ? next.delete(recipe.id) : next.add(recipe.id);
                return next;
              });
            }}
          >
            <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              {ingredientsExpanded ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              )}
            </svg>
            {ingredients.length} ingredient{ingredients.length !== 1 ? 's' : ''}
          </button>
          {ingredientsExpanded && (
            <div className="recipe-card-ingredients-list">
              {ingredients.map(ing => (
                <span key={ing.id} className="recipe-card-ingredient-tag">
                  {ing.quantity != null ? `${ing.quantity}${ing.unit ? ` ${ing.unit}` : ''} ` : ''}{ing.name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {recipe.instructions && (
        <div className="recipe-card-instructions">
          <div className="recipe-card-instructions-text">{recipe.instructions}</div>
        </div>
      )}
      
      {user?.role === 'admin' && (
        <div className="recipe-card-footer">
          <button
            className="recipe-card-delete-btn"
            onClick={() => deleteRecipe(recipe.id, recipe.name)}
            aria-label="Delete library recipe"
            title="Delete from Library"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <polyline points="3 6 5 6 21 6" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 6l-1 14H6L5 6" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 6V4h6v2" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

const renderRecipeCard = (recipe: Recipe) => {
    const isOpen = openRecipeId === recipe.id;
    const { ingredients } = recipe;
    const ingredientsExpanded = expandedIngredients.has(recipe.id);
    const searchString = ingredients.filter(i => i.name.toLowerCase() !== 'water').map(i => `"${i.name}"`).join(', ');
    const isReadonly = !!recipe.is_checked_out;

    if (!isOpen) {
      return (
        <button
          key={recipe.id}
          className={`recipe-row${isReadonly ? ' recipe-row--library' : ''}`}
          onClick={() => setOpenRecipeId(recipe.id)}
        >
          <span className="recipe-row-name">{recipe.name}</span>
          {isReadonly && (
            <span className="recipe-library-badge" title="Library recipe — tap Edit to customize">
              <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </span>
          )}
          {!isReadonly && recipe.source === 'shared' && (
            <span className="recipe-shared-badge">Shared</span>
          )}
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} className="recipe-row-chevron">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      );
    }

    const isEditing = editingRecipeId === recipe.id;

    return (
      <div key={recipe.id} className="recipe-card">
        <div className="recipe-card-header">
          {isEditing ? (
            <input
              className="recipe-edit-name-input"
              value={editForm?.name ?? ''}
              onChange={e => setEditForm(prev => prev ? { ...prev, name: e.target.value } : prev)}
              onFocus={handleInputFocus}
              placeholder="Recipe name"
              autoFocus
            />
          ) : (
            <span className="recipe-card-name">
              {recipe.name}
              {recipe.source === 'shared' && (
                <span className="recipe-shared-badge">Shared to you</span>
              )}
            </span>
          )}
          <button
            className="recipe-card-close"
            onClick={() => { cancelEdit(); setOpenRecipeId(null); }}
            aria-label="Close"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
            </svg>
          </button>
        </div>

        {isEditing && editForm ? (
          <div className="recipe-edit-body">
            <div className="recipe-edit-section-label">Ingredients</div>
            <div className="recipe-edit-ingredients">
              {editForm.ingredients.map(ing => (
                <div key={ing.tempId} className="recipe-edit-ingredient-row">
                  <input
                    className="recipe-edit-qty"
                    type="number"
                    placeholder="Qty"
                    value={ing.quantity}
                    onChange={e => updateIngredientRow(ing.tempId, 'quantity', e.target.value)}
                    onFocus={handleInputFocus}
                  />
                  <input
                    className="recipe-edit-unit"
                    placeholder="Unit"
                    value={ing.unit}
                    onChange={e => updateIngredientRow(ing.tempId, 'unit', e.target.value)}
                    onFocus={handleInputFocus}
                  />
                  <input
                    className="recipe-edit-ing-name"
                    placeholder="Ingredient"
                    value={ing.name}
                    onChange={e => updateIngredientRow(ing.tempId, 'name', e.target.value)}
                    onFocus={handleInputFocus}
                  />
                  <button
                    className="recipe-edit-remove-row"
                    onClick={() => removeIngredientRow(ing.tempId)}
                    aria-label="Remove"
                  >×</button>
                </div>
              ))}
              <button className="recipe-edit-add-row" onClick={addIngredientRow}>
                + Add ingredient
              </button>
            </div>

            <div className="recipe-edit-section-label">Instructions</div>
            <textarea
              className="recipe-edit-instructions"
              placeholder="Instructions…"
              value={editForm.instructions}
              onChange={e => setEditForm(prev => prev ? { ...prev, instructions: e.target.value } : prev)}
              onFocus={handleInputFocus}
              rows={10}
            />

            {editError && <div className="recipe-edit-error">{editError}</div>}

            <div className="recipe-edit-actions">
              <button className="recipe-edit-cancel-btn" onClick={cancelEdit} disabled={editSaving}>Cancel</button>
              <button className="recipe-edit-save-btn" onClick={saveEdit} disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {isReadonly && (
              <div className="recipe-card-library-hint">
                <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Library recipe — edit to save your own copy
              </div>
            )}
            {ingredients.length > 0 && (
              <div className="recipe-card-ingredients-section">
                <button
                  className="recipe-card-ingredients-toggle"
                  onClick={e => toggleIngredients(recipe.id, e)}
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    {ingredientsExpanded ? (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    )}
                  </svg>
                  {ingredients.length} ingredient{ingredients.length !== 1 ? 's' : ''}
                </button>
                {ingredientsExpanded && (
                  <div className="recipe-card-ingredients-list">
                    {ingredients.map(ing => (
                      <span key={ing.id} className="recipe-card-ingredient-tag">
                        {ing.quantity != null ? `${ing.quantity}${ing.unit ? ` ${ing.unit}` : ''} ` : ''}{ing.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {recipe.instructions && (
              <div className="recipe-card-instructions">{recipe.instructions}</div>
            )}

            <div className="recipe-card-footer">
              {isReadonly ? (
                <>
                  <button
                    className="recipe-card-return-btn"
                    onClick={() => handleRemoveCheckout(recipe)}
                  >
                    Return
                  </button>
                  <button
                    className="recipe-card-edit-btn"
                    onClick={() => setForkConfirmRecipe(recipe)}
                  >
                    Edit
                  </button>
                  <button
                    className="recipe-card-activate-btn"
                    onClick={() => onActivateRecipe({ name: recipe.name, description: recipe.instructions, search_string: searchString })}
                  >
                    Cook this recipe
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="recipe-card-delete-btn"
                    onClick={() => deleteRecipe(recipe.id, recipe.name)}
                    aria-label="Delete recipe"
                  >
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <polyline points="3 6 5 6 21 6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 6l-1 14H6L5 6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6V4h6v2" />
                    </svg>
                  </button>
                  <button
                    className="recipe-card-share-btn"
                    onClick={() => setShareRecipe(recipe)}
                    aria-label="Share recipe"
                  >
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                  </button>
                  <button
                    className="recipe-card-edit-btn"
                    onClick={() => startEdit(recipe)}
                  >
                    Edit
                  </button>
                  <button
                    className="recipe-card-activate-btn"
                    onClick={() => onActivateRecipe({ name: recipe.name, description: recipe.instructions, search_string: searchString })}
                  >
                    Cook this recipe
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const filteredLibraryRecipes = libraryRecipes.filter(r => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      r.name.toLowerCase().includes(q) ||
      r.ingredients.some(i => i.name.toLowerCase().includes(q))
    );
  });

  return (
    <>
      {isActive && (
        <UnifiedHeader
          title="Recipes"
          subtitle="Meal planning & discovery"
          showCollectionDropdown={false}
          showNotifications={false}
          showFontSizeToggle={true}
          onFontSizeToggle={onFontSizeToggle}
          fontSize={fontSize}
          onSockClick={() => {}} // Not used in this tab yet
          activeMode={activeMode}
          onOpenAppContext={onOpenAppContext}
          activeSpaceName={activeSpace?.name}
        />
      )}
      {groceryCollectionId && (
        <div className="recipes-menubar">
          <div className={`recipes-search-pill ${isSearchFocused ? 'focused' : ''}`}>
            <svg className="recipes-search-icon" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <circle cx="11" cy="11" r="8" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
            </svg>
            <input
              ref={searchInputRef}
              className="recipes-search-input"
              type="text"
              placeholder={isSearchFocused ? 'Search name, ingredients' : (recipeView === 'library' ? 'Search library…' : 'Search mine…')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
            />
            {searchTerm && (
              <button className="recipes-search-clear" onClick={() => setSearchTerm('')} aria-label="Clear search">
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <div className="recipes-view-toggle">
            <button
              className={`recipes-view-toggle-btn${recipeView === 'mine' ? ' recipes-view-toggle-btn--active' : ''}`}
              onClick={() => setRecipeView('mine')}
            >Mine</button>
            <button
              className={`recipes-view-toggle-btn${recipeView === 'library' ? ' recipes-view-toggle-btn--active' : ''}`}
              onClick={() => setRecipeView('library')}
            >Library</button>
          </div>
          {isActive && recipeView === 'mine' && (
            <button
              className="recipes-add-btn"
              onClick={() => setShowAddSheet(true)}
              aria-label="Add recipe"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add
            </button>
          )}
        </div>
      )}

      {groceryCollectionId && (
        <AddRecipeSheet
          isOpen={showAddSheet}
          onClose={() => setShowAddSheet(false)}
          groceryCollectionId={groceryCollectionId}
          token={token || ''}
          onRecipeAdded={fetchRecipes}
        />
      )}

      {shareRecipe && (
        <RecipeShareModal
          isOpen={true}
          onClose={() => setShareRecipe(null)}
          recipeId={shareRecipe.id}
          recipeName={shareRecipe.name}
        />
      )}

      {showCollectionShare && groceryCollectionId && (
        <CollectionShareModal
          isOpen={true}
          onClose={() => setShowCollectionShare(false)}
          collection={{ id: groceryCollectionId, name: 'Grocery', collection_type: 'grocery' }}
        />
      )}

      {/* Fork confirmation dialog */}
      {forkConfirmRecipe && (
        <div className="recipe-fork-overlay" onClick={() => setForkConfirmRecipe(null)}>
          <div className="recipe-fork-dialog" onClick={e => e.stopPropagation()}>
            <div className="recipe-fork-title">Save your own copy?</div>
            <div className="recipe-fork-body">
              Editing <strong>{forkConfirmRecipe.name}</strong> will save a personal copy. Your changes won't affect the shared library.
            </div>
            <div className="recipe-fork-actions">
              <button className="recipe-fork-cancel" onClick={() => setForkConfirmRecipe(null)}>Cancel</button>
              <button
                className="recipe-fork-confirm"
                disabled={isForkingId === forkConfirmRecipe.id}
                onClick={() => handleFork(forkConfirmRecipe)}
              >
                {isForkingId === forkConfirmRecipe.id ? 'Saving…' : 'Save copy & edit'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="recipes-tab-container">
        {!groceryCollectionId && (
          <div className="recipes-empty-state">
            <div className="recipes-empty-icon">🍳</div>
            <div className="recipes-empty-title">Setting up Kitchen…</div>
            <div className="recipes-empty-desc">Your kitchen space is being prepared.</div>
          </div>
        )}

        {/* ── Mine tab ── */}
        {groceryCollectionId && recipeView === 'mine' && (
          <>
            {loading && <div className="recipes-loading">Loading recipes…</div>}
            {error && <div className="recipes-error">{error}</div>}
            {!loading && !error && recipes.length === 0 && (
              <div className="recipes-empty-state">
                <div className="recipes-empty-icon">📖</div>
                <div className="recipes-empty-title">No recipes yet</div>
                <div className="recipes-empty-desc">
                  Go to Library to check out recipes, or [+ Add] to find online recipes
                </div>
              </div>
            )}
            {!loading && !error && recipes.length > 0 && filteredRecipes.length === 0 && (
              <div className="recipes-empty-state">
                <div className="recipes-empty-icon">🔍</div>
                <div className="recipes-empty-title">No recipes found</div>
                <div className="recipes-empty-desc">Try a different name or ingredient.</div>
              </div>
            )}
            {!loading && filteredRecipes.length > 0 && (
              <div className="recipes-list">
                {filteredRecipes.map(recipe => renderRecipeCard(recipe))}
              </div>
            )}
          </>
        )}

        {/* ── Library tab ── */}
        {groceryCollectionId && recipeView === 'library' && (
          <>
            {libraryLoading && <div className="recipes-loading">Loading library…</div>}
            {libraryError && <div className="recipes-error">{libraryError}</div>}
            {!libraryLoading && !libraryError && libraryRecipes.length === 0 && (
              <div className="recipes-empty-state">
                <div className="recipes-empty-icon">📚</div>
                <div className="recipes-empty-title">Library is empty</div>
                <div className="recipes-empty-desc">
                  Search for recipes online and add them — they'll appear here for everyone.
                </div>
              </div>
            )}
            {!libraryLoading && filteredLibraryRecipes.length > 0 && (
              <div className="recipes-list">
                {filteredLibraryRecipes.map(recipe => renderLibraryRecipeCard(recipe))}
              </div>
            )}
          </>
        )}
      
      {/* Context-sensitive help tips for Recipes */}
      {groceryCollectionId && recipeView === 'library' && (
        <ContextHelpTip
          tipId="recipes-library"
          bottomOffset={130}
          show={true}
        />
      )}
      
      {groceryCollectionId && recipeView === 'mine' && (
        <ContextHelpTip
          tipId="recipes-mine"
          bottomOffset={130}
          show={true}
        />
      )}
      </div>
    </>
  );
};

export default RecipesTab;
